import { useLocation } from 'react-router-dom';
import { useShellOwnsHierarchicalBack } from '../../app/ShellDepthBackContext.js';
import { resolveHierarchicalBackTarget } from '../../navigation/hierarchicalBack.js';
import { useTenantCode } from '../tenant/TenantProvider.js';
import { CustomerBackLink } from './CustomerBackLink.js';

export interface CustomerSubpageBackBarProps {
  /** Override resolver (e.g. checkout shop exit). `null` hides the bar. */
  readonly to?: string | null;
  /** In-flow step back when there is no route parent. */
  readonly onBack?: () => void;
  readonly label?: string;
  readonly testId?: string;
  readonly disabled?: boolean;
  /**
   * When true (default), hide if CustomerShellHeader already owns ← for this
   * depth — both link and `onBack` modes — avoids double chevron under
   * CustomerAppShell.
   */
  readonly suppressWhenShellOwns?: boolean;
}

/**
 * Renders hierarchical back when the current route (or explicit `to` / `onBack`)
 * has a parent. Hide on tab roots by leaving `to` unset and letting the resolver
 * return null — or pass `to={null}`.
 *
 * Under CustomerAppShell, when the fixed header owns ← (`ShellDepthBackProvider`),
 * this bar self-suppresses for both link and `onBack` modes (default) so
 * JourneyShell / AccountShell do not double-chevron the same parent.
 */
export function CustomerSubpageBackBar({
  to,
  onBack,
  label,
  testId,
  disabled,
  suppressWhenShellOwns = true,
}: CustomerSubpageBackBarProps): JSX.Element | null {
  const location = useLocation();
  const tenantCode = useTenantCode();
  const shellOwnsBack = useShellOwnsHierarchicalBack();

  // Shell fixed header already owns ← (hasOnStepBack / hierarchical). Suppress
  // both link and onBack content bars to avoid a double chevron.
  if (suppressWhenShellOwns && shellOwnsBack) {
    return null;
  }

  if (onBack !== undefined) {
    return (
      <div className="mb-1">
        <CustomerBackLink
          onBack={onBack}
          label={label}
          testId={testId}
          disabled={disabled}
        />
      </div>
    );
  }

  const resolved =
    to !== undefined
      ? to
      : resolveHierarchicalBackTarget(location.pathname, tenantCode, {
          search: location.search,
        })?.parentPath ?? null;

  if (resolved === null || resolved.length === 0) {
    return null;
  }

  return (
    <div className="mb-1">
      <CustomerBackLink to={resolved} label={label} testId={testId} />
    </div>
  );
}
